import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import {
  HttpError,
  type IHttpClient,
} from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IPersistentStorage } from '@webapp/infrastructure/interfaces/services/IPersistentStorage'
import { type ChangelogReleaseConstructor } from '@webapp/platform/discover-more/entities/ChangelogRelease'
import { type ChangelogReleaseApiModel } from '../../api-models/ChangelogReleaseApiModel'
import { mapChangelogReleaseApiModel } from '../../mappers/mapChangelogReleaseApiModel'

interface Dependency {
  httpClient: IHttpClient
  persistentStorage: IPersistentStorage
}

const CHANGELOG_PATH = '/changelog'

/**
 * Window of newest releases fetched in one request. Wide enough to still yield
 * ten displayable releases after the ones without a hero image are dropped.
 */
const RELEASES_WINDOW_SIZE = 20

export class ChangelogRepository {
  static readonly RELEASES_STORAGE_KEY =
    'procurify.discover-more.changelog-releases'

  constructor(private readonly deps: Dependency) {}

  /**
   * Successful responses are persisted, and a failed fetch falls back to that
   * cache so the promo surfaces show the last known releases instead of an
   * empty state. With no cache to fall back on, the error propagates and the
   * caller decides what to render.
   */
  async getLatestReleases(): Promise<ChangelogReleaseConstructor[]> {
    try {
      const releases = (
        await this.fetchReleases({ per_page: RELEASES_WINDOW_SIZE })
      ).map(mapChangelogReleaseApiModel)

      if (releases.length > 0) {
        this.deps.persistentStorage.set(
          ChangelogRepository.RELEASES_STORAGE_KEY,
          releases
        )
      }

      return releases
    } catch (err) {
      const cached = this.deps.persistentStorage.get<
        ChangelogReleaseConstructor[]
      >(ChangelogRepository.RELEASES_STORAGE_KEY)

      if (cached?.length) {
        return cached
      }

      if (err instanceof HttpError) {
        throw new RepoError({
          code: null,
          message: err.message,
          status: err.status,
          response: err.response,
          headers: err.headers,
        })
      }

      throw err
    }
  }

  private async fetchReleases(
    params: Record<string, number>
  ): Promise<ChangelogReleaseApiModel[]> {
    const res = await this.deps.httpClient.get<ChangelogReleaseApiModel[]>(
      CHANGELOG_PATH,
      { params: { orderby: 'date', order: 'desc', _embed: 1, ...params } }
    )

    return res.data ?? []
  }
}
